/**
 * WhatsApp chats to review (U9; sketch:
 * docs/design/2026-10-12-realtor-whatsapp-capture-sketch.md §3).
 *
 * Chats with numbers that are not contacts, newest first, a page at a time.
 * The realtor selects chats, sets each one's contact type and whether it is
 * an active deal, and saves them as contacts (a lead for each deal), or
 * ignores them after a confirmation. The count of chats left stays in the
 * header, so the list can be worked through over several sittings.
 *
 * A refused save or ignore keeps the selection. Chats the server says left
 * the list stay on screen marked "No longer available" and unselected;
 * numbers whose country code was refused are marked on their rows.
 */

import { Loader2, SlidersHorizontal } from "lucide-react";
import {
	type ReactElement,
	type ReactNode,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { QueryErrorState } from "@/components/ui/query-error-state";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type CountryCodeError,
	countryCodeInputId,
	initialName,
	leadTypeFor,
	REVIEW_SELECT_CLASS,
	type ReviewChoice,
	ReviewEntryRow,
} from "@/features/realtor/components/whatsapp/review-entry-row";
import { SyncProgressBar } from "@/features/realtor/components/whatsapp/sync-progress-bar";
import {
	isSyncing,
	useIgnoreWhatsappReview,
	useSaveWhatsappReview,
	useWhatsappReview,
} from "@/features/realtor/hooks/use-whatsapp";
import {
	CONTACT_TYPES,
	type ContactType,
} from "@/features/realtor/services/realtor-crm-api.service";
import {
	REVIEW_BATCH_MAX,
	type WhatsappReviewPage as ReviewPage,
	type WhatsappReviewEntry,
	type WhatsappReviewSaved,
	type WhatsappReviewSaveItem,
	whatsappReviewFailure,
} from "@/features/realtor/services/whatsapp-api.service";
import { StudioButton } from "@/features/studio/theme";
import { useIsMobile } from "@/hooks/use-mobile";

const T = "realtor.whatsappReview";
const SKELETON_ROWS = 5;

/** What "Set for all selected" applies, and what a newly selected chat starts with. */
interface BulkChoice {
	contactType: ContactType;
	deal: boolean;
}

const DEFAULT_BULK: BulkChoice = { contactType: "buyer", deal: false };

function newChoice(bulk: BulkChoice, entry: WhatsappReviewEntry): ReviewChoice {
	return {
		name: initialName(entry),
		contactType: bulk.contactType,
		deal: bulk.deal,
		leadType: leadTypeFor(bulk.contactType),
		temperature: undefined,
		countryCode: "",
	};
}

function toSaveItem(
	entry: WhatsappReviewEntry,
	choice: ReviewChoice,
): WhatsappReviewSaveItem {
	return {
		entryId: entry.id,
		contactType: choice.contactType,
		// A Name left blank saves the number, not the profile name it was
		// prefilled with.
		...(!entry.hasSavedName
			? { name: choice.name.trim() || entry.number }
			: {}),
		...(entry.phoneCountryCode === null
			? { countryCode: choice.countryCode }
			: {}),
		...(choice.deal
			? {
					lead: {
						type: choice.leadType,
						...(choice.temperature ? { temperature: choice.temperature } : {}),
					},
				}
			: {}),
	};
}

const sortTime = (entry: WhatsappReviewEntry): number =>
	Date.parse(entry.newestMessageAt ?? entry.enteredAt);

/**
 * The loaded chats, with the chats the server reported gone put back in
 * the list's order (newest first, then id) so they can be shown as such.
 */
function withUnavailable(
	entries: WhatsappReviewEntry[],
	gone: Record<string, WhatsappReviewEntry>,
): WhatsappReviewEntry[] {
	const loaded = new Set(entries.map((entry) => entry.id));
	const missing = Object.values(gone).filter((entry) => !loaded.has(entry.id));
	if (missing.length === 0) return entries;
	return [...entries, ...missing].sort(
		(a, b) => sortTime(b) - sortTime(a) || (a.id < b.id ? 1 : -1),
	);
}

function without<V>(
	record: Record<string, V>,
	ids: Iterable<string>,
): Record<string, V> {
	const next = { ...record };
	for (const id of ids) delete next[id];
	return next;
}

export function WhatsappReviewPage(): ReactElement {
	const { t } = useTranslation();
	const isMobile = useIsMobile();
	const review = useWhatsappReview();
	const save = useSaveWhatsappReview();
	const ignore = useIgnoreWhatsappReview();
	const headingRef = useRef<HTMLHeadingElement>(null);

	const [selection, setSelection] = useState<Record<string, ReviewChoice>>({});
	const [gone, setGone] = useState<Record<string, WhatsappReviewEntry>>({});
	const [countryErrors, setCountryErrors] = useState<
		Record<string, CountryCodeError>
	>({});
	const [bulk, setBulk] = useState<BulkChoice>(DEFAULT_BULK);
	const [confirmingIgnore, setConfirmingIgnore] = useState(false);
	const [bulkSheetOpen, setBulkSheetOpen] = useState(false);

	const pages = review.data?.pages;
	const firstPage = pages?.[0];
	const entries = useMemo(
		() =>
			withUnavailable(
				(pages ?? []).flatMap((page) => page.entries),
				gone,
			),
		[pages, gone],
	);
	const selectable = entries.filter((entry) => !gone[entry.id]);
	const selected = selectable.filter((entry) => selection[entry.id]);
	const busy = save.isPending || ignore.isPending;
	const overBatch = selected.length > REVIEW_BATCH_MAX;
	const actionsDisabled = busy || selected.length === 0 || overBatch;

	const focusHeading = () => headingRef.current?.focus();

	const toggle = (entry: WhatsappReviewEntry, on: boolean) => {
		setSelection((current) =>
			on
				? {
						...current,
						[entry.id]: current[entry.id] ?? newChoice(bulk, entry),
					}
				: without(current, [entry.id]),
		);
	};

	const change = (entryId: string, patch: Partial<ReviewChoice>) => {
		setSelection((current) =>
			current[entryId]
				? { ...current, [entryId]: { ...current[entryId], ...patch } }
				: current,
		);
		if ("countryCode" in patch) {
			setCountryErrors((current) => without(current, [entryId]));
		}
	};

	const allSelected =
		selectable.length > 0 && selected.length === selectable.length;
	const toggleAll = () => {
		setSelection((current) => {
			if (allSelected) {
				return without(
					current,
					selectable.map((entry) => entry.id),
				);
			}
			const next = { ...current };
			for (const entry of selectable) {
				next[entry.id] ??= newChoice(bulk, entry);
			}
			return next;
		});
	};

	const applyBulk = (patch: Partial<BulkChoice>) => {
		const nextBulk = { ...bulk, ...patch };
		setBulk(nextBulk);
		setSelection((current) => {
			const next = { ...current };
			for (const entry of selected) {
				const choice = next[entry.id];
				const dealTicked = nextBulk.deal && !choice.deal;
				next[entry.id] = {
					...choice,
					contactType: nextBulk.contactType,
					deal: nextBulk.deal,
					...(dealTicked
						? { leadType: leadTypeFor(nextBulk.contactType) }
						: {}),
				};
			}
			return next;
		});
	};

	/** Shared by save and ignore: what a refusal marks, and its message. */
	const onRefused = (error: unknown, fallback: string) => {
		const failure = whatsappReviewFailure(error);
		switch (failure.code) {
			case "REVIEW_ENTRIES_UNAVAILABLE": {
				const byId = new Map(entries.map((entry) => [entry.id, entry]));
				setGone((current) => {
					const next = { ...current };
					for (const id of failure.entryIds) {
						const entry = byId.get(id);
						if (entry) next[id] = entry;
					}
					return next;
				});
				setSelection((current) => without(current, failure.entryIds));
				toast.error(t(`${T}.errors.unavailable`));
				return;
			}
			case "COUNTRY_CODE_REQUIRED":
			case "COUNTRY_CODE_INVALID": {
				const kind: CountryCodeError =
					failure.code === "COUNTRY_CODE_REQUIRED" ? "required" : "invalid";
				setCountryErrors((current) => ({
					...current,
					...Object.fromEntries(failure.entryIds.map((id) => [id, kind])),
				}));
				toast.error(
					t(
						kind === "required"
							? `${T}.errors.countryCodeRequired`
							: `${T}.errors.countryCodeInvalid`,
					),
				);
				return;
			}
			default:
				toast.error(failure.message ?? fallback);
		}
	};

	/**
	 * After a save or ignore went through: `sent` leaves the selection and
	 * the country code errors, and every chat marked gone is cleared.
	 */
	const clearSent = (sent: string[]) => {
		setSelection((current) => without(current, sent));
		setCountryErrors((current) => without(current, sent));
		setGone({});
	};

	const onSave = () => {
		const missing = selected.filter(
			(entry) =>
				entry.phoneCountryCode === null && !selection[entry.id].countryCode,
		);
		if (missing.length > 0) {
			setCountryErrors((current) => ({
				...current,
				...Object.fromEntries(missing.map((entry) => [entry.id, "required"])),
			}));
			document.getElementById(countryCodeInputId(missing[0].id))?.focus();
			return;
		}
		const sent = selected.map((entry) => entry.id);
		save.mutate(
			selected.map((entry) => toSaveItem(entry, selection[entry.id])),
			{
				onSuccess: (saved: WhatsappReviewSaved[]) => {
					clearSent(sent);
					const contacts = t(`${T}.savedToast`, { count: saved.length });
					const leads = saved.filter((item) => item.leadId).length;
					toast.success(
						leads > 0
							? t(`${T}.savedWithLeadsToast`, {
									contacts,
									leads: t(`${T}.leadsCreated`, { count: leads }),
								})
							: contacts,
					);
					focusHeading();
				},
				onError: (error) => onRefused(error, t(`${T}.errors.saveFailed`)),
			},
		);
	};

	const onIgnore = () => {
		const sent = selected.map((entry) => entry.id);
		ignore.mutate(sent, {
			onSuccess: ({ ignored }) => {
				clearSent(sent);
				setConfirmingIgnore(false);
				toast.success(t(`${T}.ignoredToast`, { count: ignored }));
			},
			onError: (error) => {
				setConfirmingIgnore(false);
				onRefused(error, t(`${T}.errors.ignoreFailed`));
			},
		});
	};

	// Loads the next page as the end of the list scrolls into view; the
	// "Load more" button does the same for keyboards and older browsers.
	// After a next page fails, nothing loads on scroll until the button's
	// retry succeeds: a new observer reports a row already in view at once,
	// so each failure would start the next request.
	const sentinelRef = useRef<HTMLDivElement>(null);
	const {
		hasNextPage,
		isFetchingNextPage,
		isFetchNextPageError,
		fetchNextPage,
	} = review;
	useEffect(() => {
		const node = sentinelRef.current;
		if (
			!node ||
			!hasNextPage ||
			isFetchNextPageError ||
			typeof IntersectionObserver === "undefined"
		) {
			return;
		}
		const observer = new IntersectionObserver(
			([observed]) => {
				if (observed?.isIntersecting && !isFetchingNextPage) {
					void fetchNextPage();
				}
			},
			{ rootMargin: "200px" },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

	const connection = firstPage?.connection ?? null;
	const syncing = isSyncing(connection);
	const showBar = entries.length > 0;

	let body: ReactNode;
	if (review.isPending) {
		body = <LoadingRows />;
	} else if (review.isError && !review.data) {
		body = (
			<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-5">
					<QueryErrorState
						message={t(`${T}.loadFailed`)}
						onRetry={() => void review.refetch()}
						isRetrying={review.isFetching}
					/>
				</CardContent>
			</Card>
		);
	} else if (entries.length === 0) {
		body = <EmptyState connection={connection} />;
	} else {
		body = (
			<Card className="overflow-hidden bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<ul
					aria-label={t(`${T}.listLabel`)}
					className="divide-y divide-dashboard-border"
				>
					{entries.map((entry) => (
						<ReviewEntryRow
							key={entry.id}
							entry={entry}
							choice={selection[entry.id]}
							unavailable={Boolean(gone[entry.id])}
							countryCodeError={countryErrors[entry.id]}
							busy={busy}
							onToggle={(on) => toggle(entry, on)}
							onChange={(patch) => change(entry.id, patch)}
						/>
					))}
				</ul>
				{hasNextPage && (
					<div
						ref={sentinelRef}
						className="border-t border-dashboard-border p-3"
					>
						{isFetchNextPageError && (
							<p
								role="alert"
								className="mb-2 text-center text-sm text-dashboard-muted-dark"
							>
								{t(`${T}.loadMoreFailed`)}
							</p>
						)}
						<StudioButton
							variant="secondary"
							className="w-full gap-1.5"
							disabled={isFetchingNextPage}
							aria-busy={isFetchingNextPage || undefined}
							onClick={() => void fetchNextPage()}
						>
							{isFetchingNextPage && (
								<Loader2 className="h-4 w-4 animate-spin" />
							)}
							{t(isFetchNextPageError ? `${T}.loadMoreRetry` : `${T}.loadMore`)}
						</StudioButton>
					</div>
				)}
			</Card>
		);
	}

	const bulkControls = (
		<BulkControls
			bulk={bulk}
			disabled={busy || selected.length === 0}
			onChange={applyBulk}
		/>
	);

	return (
		<div
			className={`bg-dashboard-surface min-h-screen space-y-4 p-4 sm:p-6 ${showBar ? "pb-40 md:pb-6" : ""}`}
		>
			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="space-y-2 p-5 sm:p-6">
					<p className="text-xs font-mono uppercase tracking-widest text-dashboard-muted">
						{t(`${T}.eyebrow`)}
					</p>
					<h1
						ref={headingRef}
						tabIndex={-1}
						className="font-display text-3xl italic font-medium text-dashboard-text focus:outline-none"
						style={{ lineHeight: 1.1 }}
					>
						{t(`${T}.title`)}
					</h1>
					{review.data && (
						<div role="status" className="space-y-2">
							{firstPage && firstPage.total > 0 && (
								<p className="text-sm text-dashboard-muted">
									{t(`${T}.left`, { count: firstPage.total })}
								</p>
							)}
							{syncing && connection && (
								<SyncProgressBar
									syncState={connection.syncState}
									historyProgress={connection.historyProgress}
								/>
							)}
						</div>
					)}
				</CardContent>
			</Card>

			{showBar && (
				<div className="fixed inset-x-0 bottom-14 z-40 space-y-2 border-t border-dashboard-border bg-dashboard-bg p-3 shadow-[var(--dashboard-shadow-2)] md:sticky md:top-0 md:bottom-auto md:z-10 md:flex md:flex-wrap md:items-center md:gap-x-4 md:gap-y-2 md:space-y-0 md:rounded-xl md:border">
					<div className="flex items-center gap-3">
						<SelectAll
							count={selectable.length}
							checked={allSelected}
							indeterminate={selected.length > 0 && !allSelected}
							disabled={busy || selectable.length === 0}
							compact={isMobile}
							onToggle={toggleAll}
						/>
						<p
							aria-live="polite"
							className="whitespace-nowrap text-sm font-medium text-dashboard-text"
						>
							{t(`${T}.selected`, { count: selected.length })}
						</p>
						{isMobile && (
							<StudioButton
								variant="ghost"
								size="md"
								className="ml-auto min-w-11 px-2"
								aria-label={t(`${T}.setForSelected`)}
								disabled={busy || selected.length === 0}
								onClick={() => setBulkSheetOpen(true)}
							>
								<SlidersHorizontal className="h-4 w-4" aria-hidden />
							</StudioButton>
						)}
					</div>
					{!isMobile && bulkControls}
					<div className="grid grid-cols-2 gap-2 md:ml-auto md:flex">
						<StudioButton
							size={isMobile ? "md" : "sm"}
							className="gap-1.5"
							disabled={actionsDisabled}
							aria-busy={save.isPending || undefined}
							onClick={onSave}
						>
							{save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
							{t(`${T}.save`, { count: selected.length })}
						</StudioButton>
						<StudioButton
							variant="secondary"
							size={isMobile ? "md" : "sm"}
							disabled={actionsDisabled}
							onClick={() => setConfirmingIgnore(true)}
						>
							{t(`${T}.ignore`, { count: selected.length })}
						</StudioButton>
					</div>
					{overBatch && (
						<p className="text-xs text-dashboard-pill-risk md:basis-full">
							{t(`${T}.batchMax`, { max: REVIEW_BATCH_MAX })}
						</p>
					)}
				</div>
			)}

			{body}

			{isMobile && (
				<Sheet open={bulkSheetOpen} onOpenChange={setBulkSheetOpen}>
					<SheetContent
						side="bottom"
						className="mx-auto w-full max-w-lg rounded-t-xl border-dashboard-border bg-dashboard-bg"
					>
						<SheetHeader>
							<SheetTitle className="font-display text-xl italic font-medium text-dashboard-text">
								{t(`${T}.setForSelected`)}
							</SheetTitle>
							<SheetDescription className="text-sm text-dashboard-muted">
								{t(`${T}.setForSelectedHint`)}
							</SheetDescription>
						</SheetHeader>
						<div className="space-y-4 px-4 pb-4">
							{bulkControls}
							<StudioButton
								className="w-full"
								onClick={() => setBulkSheetOpen(false)}
							>
								{t(`${T}.done`)}
							</StudioButton>
						</div>
					</SheetContent>
				</Sheet>
			)}

			<AlertDialog
				open={confirmingIgnore}
				onOpenChange={(open) => !ignore.isPending && setConfirmingIgnore(open)}
			>
				<AlertDialogContent
					className="bg-dashboard-bg border-dashboard-border rounded-xl"
					onCloseAutoFocus={(event) => {
						// After an ignore nothing is selected and the trigger is
						// disabled, so the heading takes focus, as after a save.
						if (selected.length === 0) {
							event.preventDefault();
							focusHeading();
						}
					}}
				>
					<AlertDialogHeader>
						<AlertDialogTitle className="text-dashboard-text">
							{t(`${T}.ignoreDialog.title`, { count: selected.length })}
						</AlertDialogTitle>
						<AlertDialogDescription className="text-sm text-dashboard-muted">
							{t(`${T}.ignoreDialog.body`)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel
							disabled={ignore.isPending}
							className="border-dashboard-border text-dashboard-text"
						>
							{t(`${T}.ignoreDialog.cancel`)}
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={ignore.isPending}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							onClick={(event) => {
								event.preventDefault();
								onIgnore();
							}}
						>
							{ignore.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
							{t(`${T}.ignoreDialog.confirm`)}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

function SelectAll({
	count,
	checked,
	indeterminate,
	disabled,
	compact,
	onToggle,
}: {
	count: number;
	checked: boolean;
	indeterminate: boolean;
	disabled: boolean;
	/** Phones: the label reads "All", and the checkbox keeps the full name. */
	compact: boolean;
	onToggle: () => void;
}): ReactElement {
	const { t } = useTranslation();
	const ref = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (ref.current) ref.current.indeterminate = indeterminate;
	}, [indeterminate]);
	return (
		<label className="flex min-h-11 cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-dashboard-text md:min-h-9">
			<input
				ref={ref}
				type="checkbox"
				checked={checked}
				disabled={disabled}
				onChange={onToggle}
				aria-label={compact ? t(`${T}.selectAll`, { count }) : undefined}
				className="h-5 w-5 accent-[var(--color-dashboard-accent)]"
			/>
			{compact ? t(`${T}.selectAllShort`) : t(`${T}.selectAll`, { count })}
		</label>
	);
}

function BulkControls({
	bulk,
	disabled,
	onChange,
}: {
	bulk: BulkChoice;
	disabled: boolean;
	onChange: (patch: Partial<BulkChoice>) => void;
}): ReactElement {
	const { t } = useTranslation();
	return (
		<section
			aria-label={t(`${T}.setForSelected`)}
			className="flex flex-wrap items-center gap-x-3 gap-y-2"
		>
			<span className="hidden text-sm text-dashboard-muted md:inline">
				{t(`${T}.setForSelected`)}:
			</span>
			<select
				aria-label={t(`${T}.contactType`)}
				value={bulk.contactType}
				disabled={disabled}
				onChange={(event) =>
					onChange({ contactType: event.target.value as ContactType })
				}
				className={REVIEW_SELECT_CLASS}
			>
				{CONTACT_TYPES.map((type) => (
					<option key={type} value={type}>
						{t(`realtor.messages.contactTypes.${type}`)}
					</option>
				))}
			</select>
			<label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium text-dashboard-text md:min-h-9">
				<input
					type="checkbox"
					checked={bulk.deal}
					disabled={disabled}
					onChange={(event) => onChange({ deal: event.target.checked })}
					className="h-5 w-5 accent-[var(--color-dashboard-accent)]"
				/>
				{t(`${T}.activeDeal`)}
			</label>
		</section>
	);
}

function EmptyState({
	connection,
}: {
	connection: ReviewPage["connection"];
}): ReactElement {
	const { t } = useTranslation();
	let message: ReactNode;
	if (!connection || connection.status === "disconnected") {
		message = (
			<>
				<p>{t(`${T}.empty.notConnected`)}</p>
				<Link
					to="/realtor/settings"
					className="mt-2 inline-flex min-h-11 items-center font-medium text-dashboard-accent-dark underline md:min-h-0"
				>
					{t(`${T}.empty.openSettings`)}
				</Link>
			</>
		);
	} else if (isSyncing(connection)) {
		message = <p>{t(`${T}.empty.syncing`)}</p>;
	} else if (connection.syncState === "declined") {
		message = <p>{t(`${T}.empty.declined`)}</p>;
	} else {
		message = <p>{t(`${T}.empty.complete`)}</p>;
	}
	return (
		<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
			<CardContent className="p-6 text-center text-sm text-dashboard-muted">
				{message}
			</CardContent>
		</Card>
	);
}

function LoadingRows(): ReactElement {
	return (
		<Card
			className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]"
			data-testid="whatsapp-review-loading"
		>
			<CardContent className="space-y-3 p-4">
				{Array.from({ length: SKELETON_ROWS }, (_, index) => (
					<Skeleton
						// biome-ignore lint/suspicious/noArrayIndexKey: fixed placeholder rows
						key={index}
						className="h-16 w-full rounded-lg bg-dashboard-surface"
					/>
				))}
			</CardContent>
		</Card>
	);
}
